import axiosInstance from './axiosCreator';
import type {
  AppNotification,
  CreateNotificationBroadcastPayload,
  NotificationAudienceFilter,
  NotificationAudienceOptions,
  NotificationBroadcast,
  NotificationDelivery,
} from '../types/Notification';

const NOTIFICATIONS_ROUTE = '/api/v5/notifications';

export const getNotifications = async (): Promise<AppNotification[]> => {
  const { data } = await axiosInstance.get<{
    notifications: AppNotification[];
  }>(NOTIFICATIONS_ROUTE);

  return data.notifications;
};

export const getUnreadNotificationCount = async (): Promise<number> => {
  const { data } = await axiosInstance.get<{ unreadCount: number }>(
    `${NOTIFICATIONS_ROUTE}/unread-count`
  );

  return data.unreadCount;
};

export const markAllNotificationsRead = async (): Promise<number> => {
  const { data } = await axiosInstance.post<{ markedCount: number }>(
    `${NOTIFICATIONS_ROUTE}/mark-all-read`
  );

  return data.markedCount;
};

export const getNotificationBroadcasts = async (): Promise<
  NotificationBroadcast[]
> => {
  const { data } = await axiosInstance.get<{
    broadcasts: NotificationBroadcast[];
  }>(`${NOTIFICATIONS_ROUTE}/admin/broadcasts`);

  return data.broadcasts;
};

export const getNotificationAudienceOptions =
  async (): Promise<NotificationAudienceOptions> => {
    const { data } = await axiosInstance.get<NotificationAudienceOptions>(
      `${NOTIFICATIONS_ROUTE}/admin/audience-options`
    );

    return {
      instances: data.instances,
      exams: data.exams,
      admins: data.admins,
    };
  };

export const getNotificationAudienceCount = async (
  filter: NotificationAudienceFilter
): Promise<number> => {
  const { data } = await axiosInstance.get<{ count: number }>(
    `${NOTIFICATIONS_ROUTE}/admin/audience-count`,
    { params: { filter: JSON.stringify(filter) } }
  );

  return data.count;
};

export const getNotificationPushAudienceCount = async (
  filter: NotificationAudienceFilter,
  pushFilter: NotificationAudienceFilter | undefined,
  // The backend ignores pushFilter unless delivery is 'both'.
  delivery: NotificationDelivery
): Promise<number> => {
  const { data } = await axiosInstance.get<{ count: number }>(
    `${NOTIFICATIONS_ROUTE}/admin/push-audience-count`,
    {
      params: {
        filter: JSON.stringify(filter),
        ...(pushFilter ? { pushFilter: JSON.stringify(pushFilter) } : {}),
        delivery,
      },
    }
  );

  return data.count;
};

export const createNotificationBroadcast = async (
  payload: CreateNotificationBroadcastPayload
): Promise<NotificationBroadcast> => {
  const { data } = await axiosInstance.post<{
    broadcast: NotificationBroadcast;
  }>(`${NOTIFICATIONS_ROUTE}/admin/broadcasts`, payload);

  return data.broadcast;
};

export const deleteNotificationBroadcast = async (
  id: string
): Promise<void> => {
  await axiosInstance.delete(`${NOTIFICATIONS_ROUTE}/admin/broadcasts/${id}`);
};
